import {VueController} from '@/service/support/vuex-controller';
import vaultService from '@/service/vault-service';
import * as awsVars from '@/config/aws-exports';
let awsmobile = awsVars.default(process.env);
import moment from "moment";
import toMaterialStyle from 'material-color-hash';
import orgService from "@/service/org-service";
import {EventBus} from "@/service/support/event-bus";
import Auth from "@aws-amplify/auth";
import WorkflowStatesProgress from '@/components/WorkflowStatesProgress.vue';
import SendForSignature from '@/components/SendForSignature.vue';

export default VueController("Vaults", {
    components: {
        WorkflowStatesProgress,
        SendForSignature
    },
    data() {
        return {
            newVaultName: "",
            viewMode: 1,
            vaultMax: '',
            disableAddButton: false,
            snackBar: false,
            snackBarNotify: "",
            snackBarType: "",
            logoBucket: null,
	        searchVault: '',
			privateMode: false,
			showArchivedPortals: false,
			vaultsTablePage: 1,
			vaultsTablePageCount: 0,
			vaultsTableItemsPerPage: 50,
			// Unified workflow filtering (backend includes legacy org states as a
			// virtual "Default Workflow" with id 'legacy')
			availableWorkflows: [],
			selectedWorkflowFilter: null, // Selected workflow for filtering
			selectedStateFilters: [], // Multi-select state ids within the selected workflow ('not-set' allowed)
			// Column-specific filters
			filters: {
				name: '',
				tags: '',
				num_tasks_open: [0, 100],
				earliest_open_task_date: '',
				_lastChangedAt: '',
				org_id: null
			},
			detectedOrgBranding: null, // Stores branding from subdomain detection
			// Multi-select for bulk operations (desktop only)
			selectedPortals: [], // Array of selected portal IDs
			bulkUploadDialog: false,
			bulkTaskReminderDialog: false,
			// Bulk task reminders
			bulkTaskReminderSending: false,
			bulkTaskReminderProgress: 0,
			bulkTaskReminderCurrentPortal: '',
			// Bulk file upload
			bulkUploadFiles: [],
			bulkUploadSubfolder: '',
			bulkUploadProgress: false,
			bulkUploadCurrentStatus: '',
			isDraggingBulkFiles: false,
			// Bulk archive
			bulkArchiveDialog: false,
			bulkArchiveInProgress: false
        };
    },
    state: [
        "vaults",
        "cognito",
	    "modals",
        "organization",
	    "organizationId",
        "user",
	    "pageIsLoading",
	    "embeddedFullScreen"
    ],
    computed: {
    	hasExternalVault() {
    		return !this.organization || this.vaults.find(vault => vault._deleted !== 1 && vault.org_id !== this.organization.id);
	    },
	    
	    // Method to check if a workflow state ID exists in current organization states
	    isWorkflowStateOrphaned() {
	    	return (vault) => {
	    		if (!vault.workflowStateId || !this.organization || !this.organization.workflowStates) {
	    			return false;
	    		}
	    		
	    		// Check if the vault's workflowStateId exists in the organization's current workflow states
	    		const stateExists = this.organization.workflowStates.find(state => state.id === vault.workflowStateId);
	    		return !stateExists;
	    	}
	    },
	    
	    // Method to get the display name for workflow state, handling orphaned states
	    getWorkflowStateDisplayName() {
	    	return (vault) => {
	    		if (!vault.workflowStateId && !vault.workflowStateName) {
	    			return null;
	    		}
	    		
	    		// If workflowStateName exists, check if it's orphaned
	    		if (vault.workflowStateName) {
	    			if (this.isWorkflowStateOrphaned(vault)) {
	    				return vault.workflowStateName + ' (Deleted)';
	    			}
	    			return vault.workflowStateName;
	    		}
	    		
	    		// If workflowStateId exists but no name, look it up from organization states
	    		if (vault.workflowStateId && this.organization && this.organization.workflowStates) {
	    			const state = this.organization.workflowStates.find(s => s.id === vault.workflowStateId);
	    			if (state) {
	    				return state.name;
	    			}
	    			// If ID exists but state not found in organization, it's orphaned
	    			return 'Unknown State (Deleted)';
	    		}
	    		
	    		// Fallback to "Not Set" if no name is available
	    		return null;
	    	}
	    },
	    // Cache the user's favorite vault ids as a Set for O(1) lookups in
	    // sort comparators and templates that iterate every vault.
	    favoriteVaultSet() {
	    	return new Set((this.user && this.user.favoriteVaults) || []);
	    },
	    workflowStateOptions() {
	    	let options = [];
	    	
	    	// Add "Not Set" option
	    	options.push({ id: null, name: 'Not Set' });
	    	
	    	// Add workflow states from organization
	    	if (this.organization && this.organization.workflowStates) {
	    		this.organization.workflowStates.forEach(state => {
	    			options.push({ id: state.id, name: state.name });
	    		});
	    	}
	    	
	    	return options;
	    },
	    
	    // Available states for selected workflow
	    availableStatesForSelectedWorkflow() {
	    	if (!this.selectedWorkflowFilter) return [];

	    	const workflow = this.availableWorkflows.find(w => w.id === this.selectedWorkflowFilter);
	    	if (workflow && workflow.states) {
	    		return workflow.states;
	    	}

	    	return [];
	    },

	    // Portal counts per state for the selected workflow (keyed by state id, plus 'not-set')
	    workflowStateCounts() {
	    	const counts = { 'not-set': 0 };
	    	if (!this.selectedWorkflowFilter) return counts;

	    	const isLegacyWorkflow = this.selectedWorkflowFilter === 'legacy';
	    	this.availableStatesForSelectedWorkflow.forEach(state => { counts[state.id] = 0; });

	    	for (const vault of this.vaultsNotDeletedAndAccepted) {
	    		if (isLegacyWorkflow) {
	    			// The legacy "Default Workflow" also covers vaults with no workflow assigned
	    			if (vault.workflowId && vault.workflowId !== 'legacy') continue;
	    		} else if (vault.workflowId !== this.selectedWorkflowFilter) {
	    			continue;
	    		}

	    		const stateId = (isLegacyWorkflow && !vault.workflowId)
	    			? vault.workflowStateId
	    			: vault.currentWorkflowStateId;

	    		// Missing or orphaned states count as "Not Set"
	    		if (stateId !== null && stateId !== undefined && counts[stateId] !== undefined) {
	    			counts[stateId]++;
	    		} else {
	    			counts['not-set']++;
	    		}
	    	}

	    	return counts;
	    },
	    
	    // Workflow display name for vault (supports both new and legacy)
	    getVaultWorkflowDisplayName() {
	    	return (vault) => {
	    		// New workflow system
	    		if (vault.workflowId && vault.workflow) {
	    			const stateName = vault.currentWorkflowStateName || 'Not Set';
	    			return `${vault.workflow.name} - ${stateName}`;
	    		}
	    		
	    		// Legacy system
	    		if (vault.workflowStateName) {
	    			return `Default - ${vault.workflowStateName}`;
	    		}
	    		
	    		return null;
	    	}
	    },
		userCanCreateVaults() {
			return this.cognito &&
				(this.cognito.attributes["custom:mutableUserType"] === "admin" ||
					(this.cognito.attributes["custom:mutableUserType"] === "member" &&
						this.organization &&
						!!this.organization.permissions.find(perm => perm['create_vault'] === true && perm.user_id === this.user.id && perm._deleted !== 1)
					)
				);
		},
		isGuest() {
			return this.cognito && this.cognito.attributes && this.cognito.attributes["custom:mutableUserType"] === "guest";
		},
		// Check if user has any archived portals to show the toggle
		hasArchivedPortals() {
			if (!this.vaults || !this.cognito) return false;
			let userId = this.cognito.attributes.sub;
			let userType = this.cognito.attributes["custom:mutableUserType"];
			
			// Only show for members/admins who can create vaults
			if (userType === "guest") return false;
			
			return this.vaults.some(vault => 
				vault._deleted !== 1 && 
				vault.archived === true &&
				((this.user && userType !== "guest" && vault.org_id === this.user.memberOf) ||
				 (vault.permissions && vault.permissions.some(perm => 
					perm._deleted !== 1 && perm.user_id === userId
				 )))
			);
		},
		// Multi-select computed properties
		allPortalsSelected() {
			if (!this.vaultsNotDeletedAndAccepted || this.vaultsNotDeletedAndAccepted.length === 0) {
				return false;
			}
			return this.selectedPortals.length === this.vaultsNotDeletedAndAccepted.length;
		},
		somePortalsSelected() {
			return this.selectedPortals.length > 0 && this.selectedPortals.length < this.vaultsNotDeletedAndAccepted.length;
		},
		selectedPortalsData() {
			if (!this.vaults || this.selectedPortals.length === 0) return [];
			return this.vaults.filter(vault => this.selectedPortals.indexOf(vault.id) > -1);
		},
		showMultiSelectUI() {
			// Only show on desktop (md and up = 960px+)
			return this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.mdAndUp;
		},
		bulkTaskReminderInfo() {
			if (!this.selectedPortalsData || this.selectedPortalsData.length === 0) {
				return {
					portalsWithTasks: [],
					portalsWithoutTasks: [],
					totalGuests: 0,
					allGuests: []
				};
			}
			
			const portalsWithTasks = [];
			const portalsWithoutTasks = [];
			const guestMap = new Map(); // Use map to deduplicate guests across portals
			
			this.selectedPortalsData.forEach(vault => {
				if (vault.num_tasks_open && vault.num_tasks_open > 0) {
					const activeGuests = vault.permissions && vault.permissions.filter(perm => 
						perm._deleted !== 1 && 
						perm.user_email &&
						perm.invitation_accepted === "true"
					) || [];
					
					// Add guests to the map (deduplicates by email)
					activeGuests.forEach(guest => {
						if (!guestMap.has(guest.user_email)) {
							guestMap.set(guest.user_email, {
								name: guest.user_name || guest.user_email,
								email: guest.user_email
							});
						}
					});
					
					portalsWithTasks.push({
						name: vault.name,
						taskCount: vault.num_tasks_open,
						guestCount: activeGuests.length,
						guests: activeGuests.map(g => ({
							name: g.user_name || g.user_email,
							email: g.user_email
						}))
					});
				} else {
					portalsWithoutTasks.push(vault.name);
				}
			});
			
			// Convert guest map to sorted array
			const allGuests = Array.from(guestMap.values()).sort((a, b) => 
				a.name.localeCompare(b.name)
			);
			
			return {
				portalsWithTasks,
				portalsWithoutTasks,
				totalGuests: allGuests.length,
				allGuests
			};
		},
	    responsiveVaultHeaderLayout() {
			const width = this.$vuetify.breakpoint.width;
			const isEnterprise = this.organization && (this.organization.tier === 'enterprise' || this.organization.tier === 'large firm');
			const hasWorkflowStates = this.userCanCreateVaults && this.organization && this.organization.workflowStatesEnabled && isEnterprise;
			const isDesktop = width >= 960; // md and up
			
			// Responsive portal name width - smaller on mobile
			const portalNameWidth = width < 600 ? "180px" : "250px";
			
			let headers = [];
			
			// Add checkbox column on desktop only
			if (isDesktop) {
				headers.push({text: "", value: "select", sortable: false, width: "48px", align: "start"});
			}
			
			headers.push({text: "Client Portal", value: "name", sortable: true, align: "start", width: portalNameWidth});
			
			// Calculated breakpoints with Actions (60px) always visible
			// Base minimum: Portal Name (180px on mobile, 250px desktop) + Actions (60px)
			
			// Add Guests at 504px+
			if (width >= 540) {
				headers.push({text: "Guests", value: "numberOfMembers", sortable: false, width: "100px"});
			}
			
			// Add Last Modified Date at 730px+
			if (width >= 730) {
				headers.push({text: "Last Modified Date", value: "_lastChangedAt", sortable: true, width: "140px"});
			}
			
			// Add Earliest Due Date at 850px+
			if (width >= 850 && isEnterprise) {
				headers.push({text: "Earliest Due Date", value: "earliest_open_task_date", sortable: true, align: 'start', width: "130px"});
			}
			
			// Add Tasks at 1040px+
			if (width >= 1040 && isEnterprise) {
				headers.push({text: "Tasks", value: "num_tasks_open", sortable: true, width: "160px", align: "start"});
			}
			
			// Add Tags at 1425px+
			if (width >= 1425) {
				headers.push({text: "Tags", value: "tags", sortable: false, width: "130px", align: "start"});
			}
			
			// Add Workflow State at 1425px+
			if (width >= 1425 && hasWorkflowStates) {
				headers.push({text: "Workflow State", value: "workflowStateName", sortable: true, width: "130px", align: "start"});
			}
			
			// Always add Actions at the end - make sure it's always visible
			headers.push({text: "Actions", value: "actions", sortable: false, width: "60px", align: "center"});
			
			return headers;
	    },
        vaultCeiling() {
            if (this.organization) {
                if (this.organization.tier.toLowerCase() !== 'enterprise') {
                    return this.vaults.length >= this.vaultMax-20;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        },
        filterDeletedVaults() {
            if (this.vaults) {
                return this.vaults.filter(vault => vault._deleted !== 1);
            }
        },
	    vaultsNotDeletedAndAccepted() {
    		//console.time('vault filter');
        	let nvdaa = [];
        	let self = this;

		    if (self.vaults && self.cognito) {
		    	let userId = self.cognito.attributes.sub;
		    	let userType = self.cognito.attributes["custom:mutableUserType"];

				let memberCanAccessVaults = false;
				if (self.organization) {
					let memberOrgPermissions =
						self.organization.permissions.find(perm =>
							perm._deleted !== 1 && perm.invitation_accepted === "true" && perm.user_id === userId
						);
					if (memberOrgPermissions && memberOrgPermissions.access_vault === true) {
						memberCanAccessVaults = true;
					}
				}

			    nvdaa = self.vaults.filter(vault =>
				    vault._deleted !== 1
				    && ((self.user && userType !== "guest" && vault.org_id === self.user.memberOf && (userType === "admin" || memberCanAccessVaults)) ||
					vault.permissions && vault.permissions.some(perm =>
			        	perm._deleted !== 1 && 
						perm.user_id === userId &&
						// For guests, only show vaults where they've accepted the invitation
						(userType !== "guest" || perm.invitation_accepted === "true")
					))
				    && (self.showArchivedPortals || !vault.archived)
			    );
		    }

		    const favoriteSet = self.favoriteVaultSet;
		    nvdaa = nvdaa.sort((a, b) => {
		    	const aFav = favoriteSet.has(a.id);
		    	const bFav = favoriteSet.has(b.id);
			    if (aFav && !bFav) return -1;
			    if (!aFav && bFav) return 1;
			    return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
		    });
			//console.timeEnd('vault filter');
		    return nvdaa;
	    },
	    vaultsNotDeletedAndPendingInvitation() {
		    if (this.vaults && this.cognito) {
			    let userId = this.cognito.attributes.sub;
			    let userType = this.cognito.attributes["custom:mutableUserType"];

			    return this.vaults.filter(vault =>
				    vault._deleted !== 1
				    && ((userType === "guest") || (this.user && vault.org_id !== this.user.memberOf))
				    && vault.permissions && vault.permissions.some(perm =>
					    perm.user_id === userId && perm.invitation_accepted.match(/none/) && perm._deleted !== 1
				    ));
		    } else {
			    return [];
		    }
	    },
		// Computed property to ensure reactivity when any filter changes
		filteredVaults() {
			return this.customVaultFilter(this.vaultsNotDeletedAndAccepted, this.searchVault, null, null);
		},
		// Check if any filters are currently active
		hasActiveFilters() {
			return this.activeFilterCount > 0;
		},
		// Count active filters for badge display
		activeFilterCount() {
			let count = 0;
			
			// Check each filter
			if (this.filters.name && this.filters.name.trim()) count++;
			if (this.filters.tags && this.filters.tags.trim()) count++;
			if (this.filters.num_tasks_open && this.filters.num_tasks_open.length === 2 && 
				(this.filters.num_tasks_open[0] > 0 || this.filters.num_tasks_open[1] < 100)) count++;
			if (this.filters.earliest_open_task_date && this.filters.earliest_open_task_date.trim()) count++;
			if (this.filters._lastChangedAt && this.filters._lastChangedAt.trim()) count++;
			if (this.filters.org_id) count++;
			if (this.selectedWorkflowFilter) count++;
			count += this.selectedStateFilters.length;
			
			return count;
		},
		// Check if currently on a subdomain URL
		isOnSubdomain() {
			const hostname = window.location.hostname;
			
			// Ignore localhost
			if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('localhost:')) {
				return false;
			}
			
			// Check if hostname has a subdomain (at least 3 parts)
			const parts = hostname.split('.');
			if (parts.length < 3) {
				return false;
			}
			
			const subdomain = parts[0].toLowerCase();
			
		// Don't treat 'www' or 'app' as organization subdomains
		if (subdomain === 'www' || subdomain === 'app') {
			return false;
		}
		
		return true;
	},
	// MFA grace period banner
	showMfaBanner() {
		// Guests authenticate via magic link (already a form of MFA) and never
		// set up TOTP, so the MFA enforcement banner does not apply to them.
		if (this.isGuest) {
			return false;
		}
		if (!this.user || !this.user.mfaEnforcementDate || this.user.mfaSetupCompleted) {
			return false;
		}
		const now = new Date();
		const enforcementDate = new Date(this.user.mfaEnforcementDate);
		return now < enforcementDate; // Still in grace period
	},
	mfaDaysRemaining() {
		if (!this.user || !this.user.mfaEnforcementDate) return 0;
		const now = new Date();
		const enforcementDate = new Date(this.user.mfaEnforcementDate);
		const diff = enforcementDate - now;
		const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
		return days > 0 ? days : 0;
	}
    },
	watch: {
		// Reset to page 1 when search changes
		searchVault() {
			this.vaultsTablePage = 1;
		},
		// Reset to page 1 when any filter changes
		filters: {
			handler() {
				this.vaultsTablePage = 1;
			},
			deep: true
		},
		// Reset to page 1 when items per page changes
		vaultsTableItemsPerPage() {
			this.vaultsTablePage = 1;
		}
	},
	created() {
		this.logoBucket = awsmobile.aws_org_logos_s3_bucket;
		
		// Load cached branding from localStorage BEFORE rendering to prevent flash
		try {
			const cachedBranding = localStorage.getItem('publicOrgBranding');
			if (cachedBranding) {
				const brandingData = JSON.parse(cachedBranding);
				// Only use cache if it's for the current subdomain
				const hostname = window.location.hostname;
				const parts = hostname.split('.');
				const subdomain = parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'app' ? parts[0].toLowerCase() : null;
				
				if (subdomain && brandingData.subdomain === subdomain) {
					this.detectedOrgBranding = {
						id: brandingData.id,
						subdomain: brandingData.subdomain,
						logo_s3_path: brandingData.logo_s3_path,
						primaryBrandColor: brandingData.primaryBrandColor
					};
					// Note: Don't pre-populate filter here - detectSubdomainForGuestFilter will decide
					// whether to apply the filter based on user type and organization
					console.log("AllVaults: Loaded cached branding for:", subdomain);
				}
			}
		} catch (error) {
			console.error("AllVaults: Error loading cached branding:", error);
		}
	},
    mounted() {
		this.pageIsLoading = true;
        let self = this;

		let _privateMode = !!self.getLocalStoreValue("privateMode");
		self.privateMode = _privateMode;
		
		// Detect subdomain for guest users (this will refresh the cache if needed)
		this.detectSubdomainForGuestFilter();

        if (self.$route.query.embedded === "true" || self.$route.query.embedded === true) {
		    self.embeddedFullScreen = true;
	    }

        if (self.cognito && (self.cognito.attributes['custom:mutableUserType'] !== "admin" && self.cognito.attributes['custom:mutableUserType'] !== "member")) {
            this.viewMode = 1;
        } else {
	        this.viewMode = 0;
        }
        self.logoBucket = awsmobile.aws_org_logos_s3_bucket;
        self.vaults = [
            {
                name: "placeholder",
                id: 0,
                org_id: 0,
                placeholder: true
            }
        ];
		//console.time('getVaults');

        // F4: paint the first page as soon as it arrives instead of waiting
        // for fetchAllVaultPages to walk every page. For single-page orgs
        // (the common case) this is functionally identical; for huge orgs
        // it converts a long blank screen into an immediately useful list
        // that grows as later pages stream in.
        let firstPaintDone = false;
        const decorateVaults = (vaults) => {
            for (let i = 0; i < vaults.length; i++) {
                if (!Array.isArray(vaults[i].tags)) vaults[i].tags = [];
                vaults[i]._editTagsArray = [...vaults[i].tags];
            }
            return vaults;
        };

        self.fetchAllVaultPages(undefined, (page, soFar) => {
                // soFar is the same array fetchAllVaultPages mutates; assign
                // a fresh slice so Vue picks up the change reliably.
                self.vaults = decorateVaults(soFar.slice());
                if (!firstPaintDone) {
                    self.pageIsLoading = false;
                    firstPaintDone = true;
                }
            })
            .then(async response => {
				// If pagination produced zero pages (no callback fired), make
				// sure we clear the placeholder and stop the spinner.
				if (!firstPaintDone) {
					self.vaults = [];
					self.pageIsLoading = false;
				}

				//console.timeEnd('getVaults');
            	let tmpOrgId = null;
				let updateVaults = false;
            	for (let r = 0; r < response.length; r++) {

            		if (!tmpOrgId && response[r].org_id) {
			            tmpOrgId = response[r].org_id;
		            } else if (response[r].org_id !== tmpOrgId) {
            			tmpOrgId = null;
            			break;
		            }
	            }
				if(self.organization){
					for(let i=0;i<response.length; i++){
						//Double check to make sure org is on the vault level
						if(self.organization && self.organization.id && response[i].org_id === self.organization.id && response[i].orgTier !== self.organization.tier){
							updateVaults = true;
						}
					}
				}
				if(updateVaults){
					vaultService.mutation("updateVaultForTier",{}).then(response =>{
						for (let i = 0; i < self.vaults.length; i++) {
							if (self.vaults[i].org_id === self.organization.id) {
								self.vaults[i].orgTier = self.organization.tier;
							}
						}
					}).catch(e =>{
						vaultService._log("Updating vaults to new tier failed : " + JSON.stringify(e));
					});
				}
            	if (tmpOrgId) {
		            self.organizationId = tmpOrgId;
		            EventBus.$emit("refreshlazy", {oldId: "", newId: self.organizationId});
	            }

            	//DONE: If 1 vault, set org id
	            if (self.$route.query.accept && response && response.length === 1 && response[0].id === self.$route.query.accept) {
		            self.acceptInvite(response[0], true);
	            } else if (response && response.length === 1 && !response[0]._deleted && !self.isVaultPendingInvitation(response[0]) && self.cognito && self.cognito.attributes["custom:mutableUserType"] === "guest") {
					console.log(`Pushing to portal ${response[0].id} is portal deleted ? ${response[0]._deleted}`);
					self.$router.push({name: 'Portal', params: {vaultid: response[0].id, orgid: response[0].org_id, folderid: 'main'}});
	            } else {
		            if (self.organization) {
		            	self.organizationId = self.organization.logo_s3_path ? self.organization.logo_s3_path : "none";
						self.organizationId = self.organizationId.replace(".png", "");
			            switch (self.organization.tier.toLowerCase()) {
				            case 'starter':
					            self.vaultMax = 50;
					            break;
				            case 'professional':
					            self.vaultMax = 1000;
					            break;
				            case 'large firm':
					            self.vaultMax = 10000;
					            break;
				            case 'enterprise' :
					            self.vaultMax = 'unlimited'
					            break;
				            default:
					            self.vaultMax = 0;
			            }
		            } else {
		            	self.organizationId = "none";
		            }

					// F4: incremental rendering already populated self.vaults
					// page-by-page via the onPage callback above. Re-assigning
					// the final response here ensures any post-load mutations
					// (e.g. updateVaultForTier) see the canonical list, while
					// decorateVaults stays idempotent.
					self.vaults = decorateVaults(response.slice());
					self.pageIsLoading = false;
					
					// Load workflows for organization
					self.loadWorkflows();

	            }
				//console.timeEnd('mounted');
            }).catch(error => {
            	console.error(error);
	            self.pageIsLoading = false;
            });

    },
    methods: {
		// Walks the paginated getVaultsByOrgIdPaginated query until nextToken is
		// null and returns the accumulated vault list. Used instead of the
		// single-shot getVaultsByOrgId query to stay under AppSync's 1MB
		// response limit on large orgs.
		//
		// F4: optional onPage callback fires after each page so callers can
		// incrementally render. Signature: onPage(items, accumulatedSoFar).
		// Errors thrown from onPage are logged but do not abort pagination.
		// The callback is also called with an empty items[] only if the very
		// first page returns nothing; downstream code can rely on at least
		// one invocation when there is at least one page.
		async fetchAllVaultPages(templatesOnly, onPage, accumulated, nextToken) {
			accumulated = accumulated || [];
			let params = { limit: 2500 };
			if (templatesOnly === true) {
				params.templatesOnly = true;
			}
			if (nextToken) {
				params.nextToken = nextToken;
			}

			let result = await vaultService.query("getVaultsByOrgIdPaginated", params, undefined, {vuexCache: false});
			let items = result && result.items ? result.items : [];
			let responseNextToken = result && result.nextToken ? result.nextToken : null;

			accumulated = accumulated.concat(items);

			if (typeof onPage === "function") {
				try {
					onPage(items, accumulated);
				} catch (cbErr) {
					console.error("fetchAllVaultPages onPage callback error:", cbErr);
				}
			}

			if (responseNextToken) {
				return this.fetchAllVaultPages(templatesOnly, onPage, accumulated, responseNextToken);
			}

			return accumulated;
		},
		customVaultFilter(items, search, filter, headers) {
			// If no filters are active, return all items
			const hasActiveFilters = Object.values(this.filters).some(filterValue => {
				if (Array.isArray(filterValue)) {
					// For array filters (like range slider), check if it's different from default
					return filterValue.length === 2 && (filterValue[0] > 0 || filterValue[1] < 100);
				} else {
					// For string filters, check if trimmed value exists
					return filterValue && filterValue.trim();
				}
			});
			if (!hasActiveFilters && !this.selectedWorkflowFilter && (!search || !search.trim())) {
				return items;
			}

			return items.filter(item => {
				// Handle global search if provided
				if (search && search.trim()) {
					const searchLower = search.toLowerCase();
					
					// Check portal name
					const matchesName = item.name && item.name.toLowerCase().includes(searchLower);
					
					// Check organization name
					const matchesOrg = item.org_name && item.org_name.toLowerCase().includes(searchLower);
					
					// Check tags
					const matchesTags = item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchLower));
					
					// Check guest names and emails
					let matchesGuests = false;
					if (item.permissions && item.permissions.length > 0) {
						matchesGuests = item.permissions.some(perm => {
							if (perm._deleted === 1) return false;
							const matchesGuestName = perm.user_name && perm.user_name.toLowerCase().includes(searchLower);
							const matchesGuestEmail = perm.user_email && perm.user_email.toLowerCase().includes(searchLower);
							return matchesGuestName || matchesGuestEmail;
						});
					}
					
					// Check workflow state name
					let matchesWorkflowState = false;
					if (item.workflowStateName) {
						matchesWorkflowState = item.workflowStateName.toLowerCase().includes(searchLower);
					}
					
					// Check external system IDs
					let matchesExternalIds = false;
					let extIds = item.externalSystemIds;
					// Parse if stored as JSON string
					if (extIds && typeof extIds === 'string') {
						try {
							extIds = JSON.parse(extIds);
						} catch (e) {
							extIds = null;
						}
					}
					if (extIds && typeof extIds === 'object') {
						for (const [system, id] of Object.entries(extIds)) {
							if (id && id.toLowerCase().includes(searchLower)) {
								matchesExternalIds = true;
								break;
							}
							// Also match on system name
							if (system && system.toLowerCase().includes(searchLower)) {
								matchesExternalIds = true;
								break;
							}
						}
					}
					
					const matchesGlobalSearch = matchesName || matchesOrg || matchesTags || matchesGuests || matchesWorkflowState || matchesExternalIds;
					if (!matchesGlobalSearch) return false;
				}

				// Handle column-specific filters
				// Name filter
				if (this.filters.name && this.filters.name.trim()) {
					const nameFilter = this.filters.name.toLowerCase();
					if (!item.name || !item.name.toLowerCase().includes(nameFilter)) {
						return false;
					}
				}

				// Tags filter
				if (this.filters.tags && this.filters.tags.trim()) {
					const tagFilter = this.filters.tags.toLowerCase();
					if (!item.tags || !item.tags.some(tag => tag.toLowerCase().includes(tagFilter))) {
						return false;
					}
				}

				// Task completion filter
				if (this.filters.num_tasks_open && Array.isArray(this.filters.num_tasks_open)) {
					const [minPercent, maxPercent] = this.filters.num_tasks_open;
					const tasksOpen = parseInt(item.num_tasks_open) || 0;
					const tasksClosed = parseInt(item.num_tasks_closed) || 0;
					const totalTasks = tasksOpen + tasksClosed;
					const completionPercent = totalTasks > 0 ? Math.round((tasksClosed / totalTasks) * 100) : 0;
					
					// Filter out items where completion percentage is outside the selected range
					if (completionPercent < minPercent || completionPercent > maxPercent) {
						return false;
					}
				}

				// Earliest due date filter
				if (this.filters.earliest_open_task_date && this.filters.earliest_open_task_date.trim()) {
					const dateFilter = this.filters.earliest_open_task_date.toLowerCase();
					if (item.earliest_open_task_date && item.earliest_open_task_date !== -1 && item.num_tasks_open > 0) {
						const formattedDate = this.yyyymmddTOmmddyyyy(item.earliest_open_task_date, true);
						if (!formattedDate || !formattedDate.toLowerCase().includes(dateFilter)) {
							return false;
						}
					} else if (dateFilter.includes('none') || dateFilter.includes('no date')) {
						// Allow filtering for items with no due date
					} else {
						return false;
					}
				}

				// Last modified date filter
				if (this.filters._lastChangedAt && this.filters._lastChangedAt.trim()) {
					const dateFilter = this.filters._lastChangedAt.toLowerCase();
					if (item.id !== 0 && this.user) {
						const latestTimestamp = item.last_upload_at > item.last_download_at ? item.last_upload_at : item.last_download_at;
						if (latestTimestamp) {
							const formattedDate = this.momentDate(latestTimestamp, true);
							if (!formattedDate || !formattedDate.toLowerCase().includes(dateFilter)) {
								return false;
							}
						} else if (!dateFilter.includes('none') && !dateFilter.includes('no date')) {
							return false;
						}
					} else if (!dateFilter.includes('none') && !dateFilter.includes('no date')) {
						return false;
					}
				}

				// Unified workflow filter ('legacy' = virtual default workflow from org states)
				if (this.selectedWorkflowFilter) {
					const isLegacyWorkflow = this.selectedWorkflowFilter === 'legacy';

					if (isLegacyWorkflow) {
						// The legacy "Default Workflow" also covers vaults with no workflow assigned
						if (item.workflowId && item.workflowId !== 'legacy') {
							return false;
						}
					} else if (item.workflowId !== this.selectedWorkflowFilter) {
						return false;
					}

					// Multi-select state filter within the selected workflow
					if (this.selectedStateFilters.length > 0) {
						const usesLegacyStateField = isLegacyWorkflow && !item.workflowId;
						const stateId = usesLegacyStateField ? item.workflowStateId : item.currentWorkflowStateId;
						const isNotSet = usesLegacyStateField
							? (!item.workflowStateId || this.isWorkflowStateOrphaned(item))
							: (item.currentWorkflowStateId === null || item.currentWorkflowStateId === undefined);

						let matches = false;
						for (const selectedId of this.selectedStateFilters) {
							if (selectedId === 'not-set') {
								if (isNotSet) {
									matches = true;
									break;
								}
							} else if (!isNotSet && stateId === selectedId) {
								matches = true;
								break;
							}
						}

						if (!matches) {
							return false;
						}
					}
				}
				
				// Organization filter (for guests accessing via subdomain)
				if (this.filters.org_id) {
					if (!item.org_id || item.org_id !== this.filters.org_id) {
						return false;
					}
				}

				return true;
			});
		},
	    portalTableSort(items, index, isDesc) {
	    	let self = this;
	    	const favoriteSet = self.favoriteVaultSet;

		    items.sort((a, b) => {
		    	let sortResult = 0;
		    	if (!index || Array.isArray(index) && index.length === 0) {
				    sortResult = a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
			    } else {
				    for (let i = 0; i < index.length; i++) {
					    let subIndex = index[i];
					    let subIsDesc = isDesc[i];

					    //console.log(`Sort index ${subIndex} descending ${JSON.stringify(subIsDesc)} is ${typeof subIsDesc}`);

					    const aFav = favoriteSet.has(a.id);
					    const bFav = favoriteSet.has(b.id);
					    if (aFav && !bFav) {
						    sortResult = -1;
					    } else if (!aFav && bFav) {
						    sortResult = 1;
					    } else if (subIndex === "name") {
						    if (!subIsDesc) {
							    sortResult = a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
						    } else {
							    sortResult = b.name.localeCompare(a.name, 'en', { sensitivity: 'base' });
						    }
					    } else if (subIndex === "_lastChangedAt") {
						    let latestA = a.last_upload_at > a.last_download_at ? a.last_upload_at : a.last_download_at;
						    let latestB = b.last_upload_at > b.last_download_at ? b.last_upload_at : b.last_download_at;

						    if (!subIsDesc) {
							    return latestA < latestB ? -1 : 1;
						    } else {
							    return latestB < latestA ? -1 : 1;
						    }
					    } else if (subIndex === "num_tasks_open" || subIndex === "earliest_open_task_date") {
							// Calculate completion percentages
							let aTotalTasks = a.num_tasks_open + a.num_tasks_closed;
							let bTotalTasks = b.num_tasks_open + b.num_tasks_closed;
							let aCompletionPercent = aTotalTasks > 0 ? (a.num_tasks_closed / aTotalTasks) * 100 : 0;
							let bCompletionPercent = bTotalTasks > 0 ? (b.num_tasks_closed / bTotalTasks) * 100 : 0;

							// First sort by completion percentage
							if (aCompletionPercent !== bCompletionPercent) {
								return subIsDesc ? bCompletionPercent - aCompletionPercent : aCompletionPercent - bCompletionPercent;
							}

							// If completion percentages are equal, sort by date
							 if (!subIsDesc) {
							 	return a[subIndex] < b[subIndex] ? -1 : 1;
							 } else {
							 	return b[subIndex] < a[subIndex] ? -1 : 1;
							 }
						} else if (subIndex) {
						    if (!subIsDesc) {
							    return a[subIndex] < b[subIndex] ? -1 : 1;
						    } else {
							    return b[subIndex] < a[subIndex] ? -1 : 1;
						    }
					    }

					    if (sortResult !== 0) {
						    break;
					    }
				    }
			    }

			    return sortResult;
		    });

		    return items;
	    },
    	toggleFavorite(vaultId, $event) {
    		let self = this;
			let test = self.isFavorite(vaultId);
			console.log(test);
		    vaultService.mutation("setVaultAsFavorite", {'vault_id': vaultId, 'remove_vault': self.isFavorite(vaultId)}).then(response => {
		    	//console.log("Returned from set vault as favorite. " + response);
			    vaultService.query("getUserById", undefined, "user", {vuexCache: false}).then(() => {});
		    }).catch(err => {
		    	console.dir(err);
			    self.notification('error', "Unable to mark vault as a favorite.");
		    });
	    },
    	isFavorite(vaultId) {
    		return this.favoriteVaultSet.has(vaultId);
	    },
        momentDate(timestamp, dateOnly) {
            return moment.unix(timestamp / 1000).format(dateOnly ? "MM/DD/YYYY" : "MM/DD/YYYY hh:mm a");
        },
		yyyymmddTOmmddyyyy(timestamp, dateOnly) {
			let dateResult = null;
			if (timestamp) {
				let timeParts = timestamp.split("-");
				dateResult = timeParts[1] + "/" + timeParts[2] + "/" + timeParts[0];
			}
			return dateResult;
		},
        getVaultMemberInitials(vault) {
            let permissions = vault.permissions.filter(perm => perm._deleted !== 1);
            let initials = [];
			try{
				for (let p = 0; p < permissions.length; p++) {
					let username = permissions[p].user_name ? permissions[p].user_name : "";
					let email = permissions[p].user_email ? permissions[p].user_email : "";
					if(username){
						let isActive = permissions[p].invitation_accepted === "true";
						let hexColor = toMaterialStyle(username);
						initials.push({
							initials: getInitials(username),
							username: username,
							email: email,
							isActive: isActive,
							// Legacy class for backwards compatibility
							class: {
								"background-color": isActive ? hexColor.backgroundColor : "lightgray",
								"color": isActive ? hexColor.color : "black"
							}
						});
					}
				}
			}catch(e){
				console.log("Error getting initials for vault members.");
			}

            return initials;
        },
        isVaultPendingInvitation(vault) {
            let vaultIsPendingInvitation = false;
            if (this.user && this.user.vaults && this.cognito && this.cognito.attributes['custom:mutableUserType'] === "guest") {
            	let foundVault = this.user.vaults.find(testVault => testVault.vault_id === vault.id && testVault.invitation_accepted === "none" && testVault._deleted !== 1);
            	if (foundVault) {
		            vaultIsPendingInvitation = true;
	            }
            }
            return vaultIsPendingInvitation;
        },
        acceptInvite(vault, loadVault) {
        	//console.log("Accept Invitation");
        	let self = this;
            vaultService.mutation("vaultInvite", {vaultId: vault.id, acceptInvite: true}).then(() => {
	            self.notification('success', "Vault invite accepted.");
                if (loadVault === true) {
	                self.$router.push(`/org/${vault.org_id}/vault/${vault.id}/folder/main`);
                } else {
	                vaultService.query("getUserById", undefined, "user", {vuexCache: false}).then(() => {
		                self.fetchAllVaultPages().then(vaults => { self.vaults = vaults; });
	                });
                }
            }).catch(err => {
	            self.notification('error', "Unable to accept invite");
            });
        },
        denyInvite(vault) {
            vaultService.mutation("vaultInvite", {vaultId: vault.id, acceptInvite: false}).then(() => {
                vaultService.query("getUserById", undefined, "user", {vuexCache: false});
            }).catch(err => {
                this.notification('error', "Unable to dismiss invite");
            });
        },
        deleteVault(vault) {
            let self = this;
            vaultService.mutation("deleteVault", {vault_id: vault.id}).then(response => {
                self.notification('success', "Vault Deleted!");
                try {
					woopra.track("Deleted Vault", {Vault: vault.id, OrgId: self.organization.id ? self.organization.id : null });
				} catch (woopraError){
					console.error("Woopra not loaded.");
				}
                self.fetchAllVaultPages().then(vaults => { self.vaults = vaults; });
            }).catch(err => {
                this.notification('error', 'Error deleting selected vault.');
            });
        },
        async handleSignatureRequestSent(responses) {
            console.log('Signature request sent from Vaults view, responses:', responses);
            
            // Show success notification
            if (responses && responses.length > 0) {
                this.notification('success', `Signature request sent to ${responses.length} recipient(s)!`);
            }
            
            // Reload vaults to get updated signature request information
            try {
                const updatedVaults = await vaultService.query("getVaultsByOrgId", undefined, undefined, {vuexCache: false});
                if (updatedVaults) {
                    // Update the vaults in state (this will trigger reactivity)
                    this.vaults = updatedVaults;
                    console.log('Vaults reloaded after signature request sent');
                }
            } catch (error) {
                console.error('Error reloading vaults after signature request:', error);
                // Don't show error to user since the signature was sent successfully
            }
        },
        // Multi-select methods for bulk operations
        togglePortalSelection(portalId) {
            const index = this.selectedPortals.indexOf(portalId);
            if (index > -1) {
                this.selectedPortals.splice(index, 1);
            } else {
                this.selectedPortals.push(portalId);
            }
        },
        isPortalSelected(portalId) {
            return this.selectedPortals.indexOf(portalId) > -1;
        },
        selectAllPortals() {
            this.selectedPortals = this.vaultsNotDeletedAndAccepted.map(vault => vault.id);
        },
        clearPortalSelection() {
            this.selectedPortals = [];
        },
        toggleSelectAll() {
            if (this.allPortalsSelected) {
                this.clearPortalSelection();
            } else {
                this.selectAllPortals();
            }
        },
        openBulkTaskReminders() {
            if (this.selectedPortals.length === 0) {
                this.notification('warning', 'Please select at least one portal');
                return;
            }
            this.bulkTaskReminderDialog = true;
        },
        openBulkFileUpload() {
            if (this.selectedPortals.length === 0) {
                this.notification('warning', 'Please select at least one portal');
                return;
            }
            this.bulkUploadDialog = true;
            this.bulkUploadFiles = [];
            this.bulkUploadSubfolder = '';
            this.isDraggingBulkFiles = false;
        },
        openBulkSignatureRequest() {
            if (this.selectedPortals.length === 0) {
                this.notification('warning', 'Please select at least one portal');
                return;
            }
            
            // Use $set to ensure Vue reactivity
            this.$set(this.modals, 'sendForSignature', true);
            
            // Force update in next tick to ensure modal renders
            this.$nextTick(() => {
                console.log('Send for Signature modal opened in bulk mode with', this.selectedPortals.length, 'portals');
            });
        },
        openBulkArchive() {
            if (this.selectedPortals.length === 0) {
                this.notification('warning', 'Please select at least one portal');
                return;
            }
            this.bulkArchiveDialog = true;
        },
        async performBulkArchive() {
            if (this.selectedPortals.length === 0) {
                this.notification('warning', 'Please select at least one portal');
                return;
            }
            
            this.bulkArchiveInProgress = true;
            let successCount = 0;
            let failCount = 0;
            
            try {
                for (const vault of this.selectedPortalsData) {
                    try {
                        // Skip if already archived
                        if (vault.archived) {
                            successCount++;
                            continue;
                        }
                        
                        const mutationParams = {
                            "vault_id": String(vault.id),
                            "org_id": String(vault.org_id),
                            "archived": true,
                            "private_mode": Boolean(vault.private_mode) || false,
                            "hide_perms": Boolean(vault.hide_perms) || false,
                            "isTemplate": Boolean(vault.isTemplate) || false
                        };
                        
                        await vaultService.mutation("updateVaultSettings", mutationParams);
                        vault.archived = true;
                        successCount++;
                    } catch (error) {
                        console.error(`Failed to archive portal ${vault.name}:`, error);
                        failCount++;
                    }
                }
                
                // Show results
                if (failCount === 0) {
                    this.notification('success', `Successfully archived ${successCount} portal${successCount !== 1 ? 's' : ''}`);
                } else if (successCount > 0) {
                    this.notification('warning', `Archived ${successCount} portal${successCount !== 1 ? 's' : ''}, ${failCount} failed`);
                } else {
                    this.notification('error', `Failed to archive portals`);
                }
                
                // Close dialog and clear selection
                this.bulkArchiveDialog = false;
                this.bulkArchiveInProgress = false;
                this.clearPortalSelection();
                
            } catch (error) {
                console.error('Error in bulk archive:', error);
                this.notification('error', 'Failed to archive portals');
                this.bulkArchiveInProgress = false;
            }
        },
        async sendBulkTaskReminders() {
            try {
                // Filter selected portals to only include those with open tasks
                const portalsWithTasks = this.selectedPortalsData.filter(vault => vault.num_tasks_open && vault.num_tasks_open > 0);
                
                if (portalsWithTasks.length === 0) {
                    this.notification('warning', 'None of the selected portals have open tasks');
                    this.bulkTaskReminderDialog = false;
                    return;
                }
                
                // Show progress
                this.bulkTaskReminderSending = true;
                this.bulkTaskReminderProgress = 0;
                
                let totalGuestCount = 0;
                let successCount = 0;
                let failCount = 0;
                
                // Send reminders for each portal with open tasks
                for (let i = 0; i < portalsWithTasks.length; i++) {
                    const vault = portalsWithTasks[i];
                    
                    // Update progress
                    this.bulkTaskReminderProgress = Math.round(((i) / portalsWithTasks.length) * 100);
                    this.bulkTaskReminderCurrentPortal = vault.name;
                    try {
                        // Get active guest emails from portal permissions
                        console.log(`Processing portal: ${vault.name}`);
                        console.log(`  Total permissions:`, vault.permissions ? vault.permissions.length : 0);
                        
                        const guestEmails = vault.permissions && vault.permissions
                            .filter(perm => {
                                const isValid = perm._deleted !== 1 && 
                                    perm.user_email &&
                                    perm.invitation_accepted === "true";
                                console.log(`  Permission: ${perm.user_email}, deleted: ${perm._deleted}, accepted: ${perm.invitation_accepted}, valid: ${isValid}`);
                                return isValid;
                            })
                            .map(perm => perm.user_email) || [];
                        
                        console.log(`  Active guest emails for ${vault.name}:`, guestEmails);
                        
                        if (guestEmails.length === 0) {
                            console.log(`⚠️ Skipping portal ${vault.name} - no active guests`);
                            continue;
                        }
                        
                        totalGuestCount += guestEmails.length;
                        
                        // Send notification email for this portal
                        // Using the newAuditNotificationAction mutation similar to individual portal notifications
                        const toEmailsString = guestEmails.join(',');
                        const now = new Date();
                        
                        // Fetch tasks for this portal
                        const tasksResponse = await vaultService.query("getTasksByVaultId", {
                            vault_id: vault.id,
                            show_deleted: false
                        }, undefined, {vuexCache: false});
                        
                        // Build HTML list of open tasks
                        let openTaskHtml = "";
                        if (tasksResponse && tasksResponse.length > 0) {
                            const openTasks = tasksResponse.filter(task => 
                                task._deleted !== 1 && task.status === "Open"
                            );
                            
                            if (openTasks.length > 0) {
                                openTaskHtml = '<ul>';
                                openTasks.forEach(task => {
                                    openTaskHtml += `<li>${task.title}</li>`;
                                });
                                openTaskHtml += '</ul>';
                            }
                        }
                        
                        // Get task reminder template from organization or use default
                        let taskMessage = '';
                        if (this.organization && this.organization.taskReminder_intro) {
                            // Use organization's custom template
                            taskMessage = this.organization.taskReminder_intro;
                            // Replace template variables
                            taskMessage = taskMessage.replace(/{{portalName}}/g, vault.name);
                            taskMessage = taskMessage.replace(/{{orgName}}/g, this.organization.companyName || '');
                            taskMessage = taskMessage.replace(/{{taskCount}}/g, vault.num_tasks_open);
                            taskMessage = taskMessage.replace(/{{openTasks}}/g, openTaskHtml);
                            taskMessage = taskMessage.replace(/{{ openTasks }}/g, openTaskHtml);
                            
                            // Add closing if exists
                            if (this.organization.taskReminder_closing) {
                                taskMessage += '\n\n' + this.organization.taskReminder_closing;
                            }
                        } else {
                            // Use default message with task list
                            taskMessage = `<p>Please complete the following tasks for Portal "${vault.name}".</p>${openTaskHtml}<p>Please visit your portal to review and complete these tasks.</p>`;
                        }
                        
                        console.log(`Sending task reminder for portal ${vault.name} to ${guestEmails.length} guest(s)`);
                        
                        await vaultService.mutation("newAuditNotificationAction", {
                            action: "custom_upload",
                            vault_id: vault.id,
                            org_id: vault.org_id || '',
                            description: taskMessage,
                            filename: toEmailsString,
                            action_date: now.getTime().toString()
                        });
                        
                        console.log(`✅ Sent task reminder for portal ${vault.name}`);
                        successCount++;
                    } catch (error) {
                        console.error(`❌ Error sending reminder for portal ${vault.name}:`, error);
                        console.error('Error details:', error.message || error);
                        failCount++;
                    }
                }
                
                // Log summary
                // Complete progress
                this.bulkTaskReminderProgress = 100;
                
                console.log(`\n📊 Bulk Task Reminder Summary:`);
                console.log(`  - Portals with tasks: ${portalsWithTasks.length}`);
                console.log(`  - Successfully sent: ${successCount}`);
                console.log(`  - Failed: ${failCount}`);
                console.log(`  - Skipped (no guests): ${portalsWithTasks.length - successCount - failCount}`);
                console.log(`  - Total guests notified: ${totalGuestCount}\n`);
                
                // Show appropriate message based on results
                if (successCount === 0 && failCount === 0) {
                    // All portals were skipped (no active guests)
                    this.notification('warning', 'No task reminders sent - selected portals have no active guests');
                } else if (successCount > 0 && failCount === 0) {
                    // All successful
                    this.notification('success', `Sent task reminders to ${totalGuestCount} guest${totalGuestCount !== 1 ? 's' : ''} across ${successCount} portal${successCount !== 1 ? 's' : ''}`);
                } else if (successCount > 0 && failCount > 0) {
                    // Partial success
                    this.notification('warning', `Sent reminders to ${successCount} portal${successCount !== 1 ? 's' : ''}, but ${failCount} failed`);
                } else {
                    // All failed
                    this.notification('error', `Failed to send reminders - ${failCount} portal${failCount !== 1 ? 's' : ''} had errors`);
                }
                
                // Close dialog and clear selection
                this.bulkTaskReminderDialog = false;
                this.bulkTaskReminderSending = false;
                this.bulkTaskReminderProgress = 0;
                this.bulkTaskReminderCurrentPortal = '';
                this.clearPortalSelection();
                
            } catch (error) {
                console.error('Error in bulk task reminders:', error);
                this.notification('error', 'Failed to send bulk task reminders');
                this.bulkTaskReminderSending = false;
                this.bulkTaskReminderProgress = 0;
                this.bulkTaskReminderCurrentPortal = '';
            }
        },
        onBulkDragEnter(event) {
            event.preventDefault();
            event.stopPropagation();
            this.isDraggingBulkFiles = true;
        },
        onBulkDragOver(event) {
            event.preventDefault();
            event.stopPropagation();
            this.isDraggingBulkFiles = true;
        },
        onBulkDragLeave(event) {
            // Only set to false if we're actually leaving the card (not just entering a child element)
            const cardElement = event.currentTarget;
            const relatedTarget = event.relatedTarget;
            
            // Only clear drag state if we're leaving the card entirely
            if (!relatedTarget || !cardElement.contains(relatedTarget)) {
                this.isDraggingBulkFiles = false;
            }
        },
        onBulkDrop(event) {
            event.preventDefault();
            event.stopPropagation();
            this.isDraggingBulkFiles = false;
            
            if (!event.dataTransfer) {
                console.error('❌ No dataTransfer object');
                return;
            }
            
            const files = event.dataTransfer.files;
            console.log('📦 Files detected in drop:', files ? files.length : 0);
            
            if (files && files.length > 0) {
                // Append new files to existing files instead of replacing
                const newFiles = Array.from(files);
                console.log(`  Dropped ${newFiles.length} file(s):`);
                newFiles.forEach((file, index) => {
                    console.log(`    ${index + 1}. ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
                });
                
                this.bulkUploadFiles = [...this.bulkUploadFiles, ...newFiles];
                console.log(`✅ Total files in upload queue: ${this.bulkUploadFiles.length}`);
            } else {
                console.warn('⚠️ No files in drop event');
                console.warn('DataTransfer types:', event.dataTransfer ? event.dataTransfer.types : 'no dataTransfer');
            }
        },
        handleBulkFilesSelect(event) {
            const files = event.target.files;
            
            if (files && files.length > 0) {
                // Append new files to existing files instead of replacing
                const newFiles = Array.from(files);
                this.bulkUploadFiles = [...this.bulkUploadFiles, ...newFiles];
                console.log(`📎 Added ${newFiles.length} file(s). Total: ${this.bulkUploadFiles.length}`);
            }
            // Reset input so same file can be selected again
            event.target.value = '';
        },
        removeBulkUploadFile(index) {
            this.bulkUploadFiles.splice(index, 1);
        },
        async performBulkFileUpload() {
            if (this.bulkUploadFiles.length === 0) {
                this.notification('warning', 'Please select files to upload');
                return;
            }
            
            this.bulkUploadProgress = true;
            let successCount = 0;
            let failCount = 0;
            const totalUploads = this.bulkUploadFiles.length * this.selectedPortalsData.length;
            let currentUpload = 0;
            
            try {
                // Process each file
                for (const file of this.bulkUploadFiles) {
                    // Upload to each selected portal
                    for (const vault of this.selectedPortalsData) {
                        currentUpload++;
                        this.bulkUploadCurrentStatus = `Uploading ${file.name} to ${vault.name} (${currentUpload}/${totalUploads})...`;
                        
                        try {
                            console.log(`Starting upload of ${file.name} to ${vault.name}`);
                            
                            // Get vault token
                            const tokenResponse = await vaultService.getVaultToken(vault);
                            let token = tokenResponse && tokenResponse.token && tokenResponse.token.Credentials ? tokenResponse.token.Credentials : tokenResponse.Credentials;
                            
                            if (!token) {
                                throw new Error('Failed to get vault token');
                            }
                            console.log(`Got vault token for ${vault.name}`);
                            
                            // Generate file ID
                            const fileId = this.generateUUID();
                            const fileExtension = file.name && file.name.indexOf('.') > -1 ? file.name.split('.').pop() : '';
                            console.log(`Generated file ID: ${fileId}, extension: ${fileExtension}`);
                            
                            // Find or create subfolder if specified
                            let parentFolderId = null; // null = root folder
                            if (this.bulkUploadSubfolder && this.bulkUploadSubfolder.trim()) {
                                const folderPath = this.bulkUploadSubfolder.trim();
                                console.log(`Creating folder path: ${folderPath}`);
                                parentFolderId = await this.createFolderPath(vault, folderPath);
                                console.log(`Folder created/found with ID: ${parentFolderId}`);
                            }
                            
                            // Upload to S3
                            console.log(`Uploading ${file.name} to S3...`);
                            await this.uploadFileToS3(file, vault, token, fileId);
                            console.log(`S3 upload complete for ${file.name}`);
                            
                            // Register file in database
                            const newFile = {
                                id: fileId,
                                filename: file.name,
                                extension: fileExtension,
                                size: parseFloat(file.size),
                                parent_folder_file_id: parentFolderId,
                                vault_id: vault.id,
                                org_id: vault.org_id
                            };
                            
                            console.log(`Registering file in database:`, newFile);
                            const fileResponse = await vaultService.mutation("fileUploaded", newFile);
                            console.log(`Successfully uploaded ${file.name} to ${vault.name}`, fileResponse);
                            successCount++;
                            
                        } catch (error) {
                            console.error(`Error uploading ${file.name} to ${vault.name}:`, error);
                            console.error('Error details:', {
                                message: error.message,
                                stack: error.stack,
                                name: error.name
                            });
                            failCount++;
                        }
                    }
                }
                
                // Show results
                if (successCount > 0) {
                    this.notification('success', `Successfully uploaded ${successCount} file${successCount !== 1 ? 's' : ''}`);
                    
                    // Reload vaults to refresh file counts
                    try {
                        await vaultService.query("getVaultsByOrgId", undefined, "vaults", {vuexCache: false});
                    } catch (error) {
                        console.error('Error reloading vaults after upload:', error);
                    }
                }
                
                if (failCount > 0) {
                    this.notification('warning', `${failCount} upload${failCount !== 1 ? 's' : ''} failed`);
                }
                
                // Close dialog and clear
                this.bulkUploadDialog = false;
                this.bulkUploadFiles = [];
                this.bulkUploadSubfolder = '';
                this.clearPortalSelection();
                
            } catch (error) {
                console.error('Error in bulk file upload:', error);
                this.notification('error', 'Bulk file upload failed');
            } finally {
                this.bulkUploadProgress = false;
                this.bulkUploadCurrentStatus = '';
            }
        },
        async createFolderPath(vault, folderPath) {
            // Create folder path like "documents/2024" and return the final folder ID
            const folderNames = folderPath.split('/').filter(name => name.trim());
            let currentParentId = null; // null = root folder
            
            for (const folderName of folderNames) {
                const trimmedName = folderName.trim();
                if (!trimmedName) continue;
                
                // Check if folder already exists
                const vaultFiles = await vaultService.query("getFilesByVaultId", {
                    vault_id: vault.id,
                    show_deleted: false
                }, undefined, {vuexCache: false});
                
                const existingFolder = vaultFiles && vaultFiles.find(file =>
                    file.extension === "fld" &&
                    (file.parent_folder_file_id ? file.parent_folder_file_id : null) === currentParentId &&
                    file.name && file.name.toLowerCase() === trimmedName.toLowerCase() &&
                    file._deleted !== 1
                );
                
                if (existingFolder) {
                    console.log(`Found existing folder: ${trimmedName} with ID: ${existingFolder.id}`);
                    currentParentId = existingFolder.id;
                } else {
                    // Create new folder
                    const newFolderId = this.generateUUID();
                    const newFolder = {
                        id: newFolderId,
                        filename: trimmedName,
                        extension: "fld",
                        size: 0,
                        parent_folder_file_id: currentParentId,
                        vault_id: vault.id,
                        org_id: vault.org_id
                    };
                    
                    console.log(`Creating new folder: ${trimmedName} with parent: ${currentParentId}`);
                    const folderResponse = await vaultService.mutation("fileUploaded", newFolder);
                    console.log(`Created folder: ${trimmedName} in vault ${vault.name}`, folderResponse);
                    currentParentId = newFolderId;
                }
            }
            
            return currentParentId;
        },
        async uploadFileToS3(file, vault, token, fileId) {
            const AWS = require('aws-sdk');
            let S3 = require('aws-sdk/clients/s3');
            
            return new Promise((resolve, reject) => {
                const tempCredentials = new AWS.Credentials({
                    accessKeyId: token.AccessKeyId,
                    secretAccessKey: token.SecretAccessKey,
                    sessionToken: token.SessionToken
                });
                
                try {
                    const s3 = new S3({
                        credentials: tempCredentials,
                        apiVersion: "2006-03-01",
                        region: "us-east-1",
                        params: {
                            Bucket: awsmobile.aws_user_files_s3_bucket
                        }
                    });
                    
                    const filePath = `${vault.org_id}/${vault.id}/${fileId}`;
                    
                    s3.upload({
                        Bucket: awsmobile.aws_user_files_s3_bucket,
                        region: "us-east-1",
                        Key: filePath,
                        Body: file,
                        Metadata: {
                            'filename': encodeURI(file.name)
                        }
                    }, function(err, data) {
                        if (err) {
                            if (err.code !== "RequestAbortedError") {
                                console.error("S3 Upload Error:", err);
                                reject(err);
                            }
                        } else {
                            resolve(data);
                        }
                    });
                } catch (s3Error) {
                    console.error("Unable to load s3 instance for upload:", s3Error);
                    reject(s3Error);
                }
            });
        },
        generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        },
        handleEditPdf(fileInfo) {
            // Close the send for signature modal
            this.modals.sendForSignature = false;
            
            // Navigate to the vault with the file
            // The fileInfo should have vault_id or portal_id
            const vaultId = fileInfo.vault_id || fileInfo.portal_id;
            if (vaultId) {
                // Navigate to the vault page with the file pre-selected
                // Store the file info so it can be opened when the vault page loads
                this.$router.push({
                    name: 'vault',
                    params: { id: vaultId },
                    query: { openFile: fileInfo.id }
                });
            } else {
                this.notification('error', 'Unable to open PDF file - vault not found');
            }
        },
        archiveVault(vault) {
            let self = this;
            
            // Convert vault.archived to a proper boolean and determine new state
            let currentlyArchived = Boolean(vault.archived);
            let newArchivedState = !currentlyArchived;
            let actionText = newArchivedState ? 'archived' : 'unarchived';
            
            // The Lambda function requires ALL boolean fields to be passed as proper booleans
            let mutationParams = {
                "vault_id": String(vault.id),
                "org_id": String(vault.org_id),
                "archived": newArchivedState,
                "private_mode": Boolean(vault.private_mode) || false,
                "hide_perms": Boolean(vault.hide_perms) || false,
                "isTemplate": Boolean(vault.isTemplate) || false
            };
            
            vaultService.mutation("updateVaultSettings", mutationParams).then(function () {
                vault.archived = newArchivedState;
                self.notification('success', `Portal ${actionText} successfully.`);
            }).catch(function (error) {
                self.notification('error', `Unable to ${newArchivedState ? 'archive' : 'unarchive'} portal.`);
                console.error("updateVaultSettings Error:", error);
            });
        },
        createNewVault() {
            let self = this;
            if (self.newVaultName) {
                 vaultService.mutation("createVault", {name: self.newVaultName.trim(), org_id: self.organization.id}).then(result => {
					try {
						woopra.track("Portal Created", {Portal_Name : self.newVaultName, OrgId : self.organization.id});
					} catch (woopraError) {
						console.error("Woopra not loaded.");
					}
                    self.newVaultName = "";

                    self.notification('success', "Portal Created!");
                    self.fetchAllVaultPages().then(vaults => { self.vaults = vaults; });
                }).catch(err => {
                    self.notification('error', "Error creating new Client Portal.");
                });
            } else {
                self.notification('error', "Please enter a name for the new Client Portal.");
            }
        },
	    signOutUser() {
		    this.cache = {};
		    Auth.signOut({ global: true })
			    .then(data => {
				    EventBus.$emit('authState', {
					    status: 'signedOut'
				    });
			    })
			    .catch(err => console.log(err));
	    },
		togglePrivacyMode(value) {
			this.setLocalStoreValue("privateMode", value);
		},
		toggleArchivedPortals(value) {
			this.showArchivedPortals = value;
		},
		setLocalStoreValue(cname, cvalue) {
			localStorage.setItem(cname, cvalue);
		},
		getLocalStoreValue(cname) {
			let cvalue = null;
			if (localStorage[cname] === "true") {
				cvalue = true;
			} else if (localStorage[cname] === "false") {
				cvalue = false;
			} else {
				cvalue = localStorage[cname];
			}
			return cvalue;
		},
        saveTags(vault) {
            let self = this;
            let tags = Array.isArray(vault._editTagsArray) ? vault._editTagsArray.filter(Boolean) : [];
            vaultService.mutation('updateVaultSettings', {
                org_id: vault.org_id,
                vault_id: vault.id,
                tags: tags,
                archived: Boolean(vault.archived) || false,
                private_mode: Boolean(vault.private_mode) || false,
                hide_perms: Boolean(vault.hide_perms) || false,
                isTemplate: Boolean(vault.isTemplate) || false
            }).then(() => {
                self.snackBar = true;
                self.snackBarNotify = 'Tags updated!';
                self.snackBarType = 'success';
                self.$set(vault, 'tags', tags);
                self.$set(vault, '_editTagsArray', [...tags]);
            }).catch(() => {
                self.snackBar = true;
                self.snackBarNotify = 'Failed to update tags.';
                self.snackBarType = 'error';
            });
        },
		clearAllFilters() {
			this.searchVault = '';
			this.selectedWorkflowFilter = null;
			this.selectedStateFilters = [];
			this.filters = {
				name: '',
				tags: '',
				num_tasks_open: [0, 100],
				earliest_open_task_date: '',
				_lastChangedAt: ''
			};
			this.vaultsTablePage = 1;
		},
		
	// Load workflows for filtering
	loadWorkflows() {
		// Skip if no org ID or if it's the placeholder "none" value
		if (!this.organizationId || this.organizationId === "none") return;
		
		orgService.query("getWorkflowsByOrgId", {
				org_id: this.organizationId
			}).then(workflows => {
				this.availableWorkflows = workflows || [];
			}).catch(error => {
				console.error("Error loading workflows:", error);
				this.availableWorkflows = [];
			});
		},
		
		// Handle workflow filter change
		onWorkflowFilterChange() {
			// Clear state selections when workflow changes
			this.selectedStateFilters = [];
			this.vaultsTablePage = 1;
		},
		
		// Clear workflow filters
		clearWorkflowFilters() {
			this.selectedWorkflowFilter = null;
			this.selectedStateFilters = [];
			this.vaultsTablePage = 1;
		},
		toggleWorkflowStateFilter(stateId) {
			const index = this.selectedStateFilters.indexOf(stateId);
			if (index >= 0) {
				this.selectedStateFilters.splice(index, 1);
			} else {
				this.selectedStateFilters.push(stateId);
			}
			this.vaultsTablePage = 1;
		},
		clearStateFilters() {
			this.selectedStateFilters = [];
			this.vaultsTablePage = 1;
		},
		updateVaultWorkflowState(vault, newStateId) {
			let self = this;
			
			// New workflow system
			if (vault.workflowId && vault.workflow) {
				// Find the new state name
				let newStateName = null;
				if (newStateId !== null && newStateId !== undefined && vault.workflow.states) {
					const newState = vault.workflow.states.find(state => state.id === newStateId);
					newStateName = newState ? newState.name : null;
				}
				
				// Update the vault
				vaultService.mutation("updateVaultSettings", {
					org_id: vault.org_id,
					vault_id: vault.id,
					currentWorkflowStateId: newStateId,
					archived: Boolean(vault.archived) || false,
					private_mode: Boolean(vault.private_mode) || false,
					hide_perms: Boolean(vault.hide_perms) || false,
					isTemplate: Boolean(vault.isTemplate) || false,
					tags: vault.tags || []
				}).then(() => {
					// Update the local vault object
					self.$set(vault, 'currentWorkflowStateId', newStateId);
					self.$set(vault, 'currentWorkflowStateName', newStateName);
					
					self.snackBar = true;
					if (newStateName) {
						self.snackBarNotify = `Portal workflow state updated to: ${newStateName}`;
					} else {
						self.snackBarNotify = 'Portal workflow state cleared.';
					}
					self.snackBarType = 'success';
				}).catch((error) => {
					console.error("Error updating vault workflow state:", error);
					self.snackBar = true;
					self.snackBarNotify = 'Failed to update workflow state.';
					self.snackBarType = 'error';
				});
				return;
			}
			
			// Legacy workflow system
			// Find the new state name
			let newStateName = null;
			if (newStateId && this.organization && this.organization.workflowStates) {
				const newState = this.organization.workflowStates.find(state => state.id === newStateId);
				newStateName = newState ? newState.name : null;
			}
			
			// Update the vault
			vaultService.mutation("updateVaultSettings", {
				org_id: vault.org_id,
				vault_id: vault.id,
				workflowStateId: newStateId,
				workflowStateName: newStateName,
				archived: Boolean(vault.archived) || false,
				private_mode: Boolean(vault.private_mode) || false,
				hide_perms: Boolean(vault.hide_perms) || false,
				isTemplate: Boolean(vault.isTemplate) || false,
				tags: vault.tags || []
			}).then(() => {
				// Update the local vault object
				self.$set(vault, 'workflowStateId', newStateId);
				self.$set(vault, 'workflowStateName', newStateName);
				
				self.snackBar = true;
				if (newStateName) {
					self.snackBarNotify = `Portal workflow state updated to: ${newStateName}`;
				} else {
					self.snackBarNotify = 'Portal workflow state cleared.';
				}
				self.snackBarType = 'success';
			}).catch((error) => {
				console.error("Error updating vault workflow state:", error);
				self.snackBar = true;
				self.snackBarNotify = 'Failed to update workflow state.';
				self.snackBarType = 'error';
			});
		},
		
		// Subdomain Detection and Org Filter Methods
		async detectSubdomainForGuestFilter() {
			try {
				if (!this.cognito || !this.cognito.attributes) {
					return;
				}
				
				const userType = this.cognito.attributes["custom:mutableUserType"];
				
				// Parse hostname to extract subdomain
				const hostname = window.location.hostname;
				
				// Ignore localhost
				if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('localhost:')) {
					return;
				}
				
				// Check if hostname has a subdomain (at least 3 parts)
				const parts = hostname.split('.');
				if (parts.length < 3) {
					return; // No subdomain
				}
				
				const subdomain = parts[0].toLowerCase();
				
				// Don't treat 'www' or 'app' as organization subdomains
				if (subdomain === 'www' || subdomain === 'app') {
					return;
				}
				
				console.log("Detected subdomain:", subdomain);
				
				// Call public REST API endpoint (no auth required)
				try {
					const restApiUrl = awsmobile.aws_rest_api_endpoint || awsmobile.aws_appsync_graphqlEndpoint.replace('/graphql', '');
					const response = await fetch(`${restApiUrl}/public/org-by-subdomain?subdomain=${encodeURIComponent(subdomain)}`);
					
					if (response.ok) {
						const publicOrg = await response.json();
						
						if (publicOrg && publicOrg.id) {
							// Store branding info
							this.detectedOrgBranding = {
								id: publicOrg.id,
								subdomain: publicOrg.subdomain,
								logo_s3_path: publicOrg.logo_s3_path,
								primaryBrandColor: publicOrg.primaryBrandColor
							};
							
							// Cache branding data for instant load on future visits
							try {
								localStorage.setItem('publicOrgBranding', JSON.stringify(this.detectedOrgBranding));
							} catch (storageError) {
								console.error("Error caching branding data:", storageError);
							}
							
							// Determine if we should apply the filter:
							// 1. Always apply for guests
							// 2. Apply for admin/member if subdomain org is different from their org
							// 3. Don't apply if admin/member is visiting their own org's subdomain
							let shouldApplyFilter = false;
							
							if (userType === "guest") {
								// Guests always get filtered
								shouldApplyFilter = true;
							} else if (userType === "admin" || userType === "member") {
								// Check if the subdomain org is different from their own org
								if (this.organization && this.organization.id !== publicOrg.id) {
									// Visiting another organization's subdomain - apply filter
									shouldApplyFilter = true;
								}
								// If organization.id === publicOrg.id, don't apply filter (visiting own org)
							}
							
							if (shouldApplyFilter) {
								this.filters.org_id = publicOrg.id;
								console.log("Applied organization filter from subdomain", publicOrg);
							} else {
								console.log("Not applying filter - user is member/admin of this organization");
							}
						} else {
							// Subdomain not found, redirect to app
							console.log("No organization found for subdomain:", subdomain, "- redirecting to app");
							const domain = parts.slice(1).join('.');
							const port = window.location.port ? ':' + window.location.port : '';
							window.location.href = window.location.protocol + "//app." + domain + port + window.location.pathname + window.location.search;
						}
					} else if (response.status === 404) {
						// Subdomain not found, redirect to app
						console.log("Subdomain not found:", subdomain, "- redirecting to app");
						const domain = parts.slice(1).join('.');
						const port = window.location.port ? ':' + window.location.port : '';
						window.location.href = window.location.protocol + "//app." + domain + port + window.location.pathname + window.location.search;
					} else {
						console.error("Error fetching subdomain branding, status:", response.status);
					}
				} catch (error) {
					console.error("Error looking up organization by subdomain:", error);
					// Don't redirect on fetch error
				}
			} catch (error) {
				console.error("Error detecting subdomain for guest filter:", error);
				// Fail gracefully
			}
		},
		
		clearOrgFilter() {
			this.filters.org_id = null;
			this.vaultsTablePage = 1;
		},
		
		getOrgLogoUrl(logo_s3_path) {
			if (!logo_s3_path) return null;
			// Construct CloudFront URL for the logo
			const logosDomain = awsmobile.aws_cloudfront_logos_domain;
			return `https://${logosDomain}/${logo_s3_path}`;
		}
    }
});

function getInitials(value) {
    let initials = [];
    try {
	    initials = value.toUpperCase().replace(/[^a-zA-Z- ]/g, "").match(/\b\w/g);
	    if (initials === null || initials.length === 0) {
		    initials = value.substring(0, 1).toUpperCase();
	    }
    } catch(e) {
    	console.error("Unable to get initials.");
    }

    return initials.join('');
}

function stringToColour(str) {
    var hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    var colour = '#';
    for (let i = 0; i < 3; i++) {
        let value = (hash >> (i * 8)) & 0xFF;
        colour += ('00' + value.toString(16)).substr(-2);
    }
    return colour;
}

function lightOrDark(color) {

    // Variables for red, green, blue values
    var r, g, b, hsp;

    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {

        // If RGB --> store the red, green, blue values in separate variables
        color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

        r = color[1];
        g = color[2];
        b = color[3];
    } else {

        // If hex --> Convert it to RGB: http://gist.github.com/983661
        color = +("0x" + color.slice(1).replace(
            color.length < 5 && /./g, '$&$&'));

        r = color >> 16;
        g = color >> 8 & 255;
        b = color & 255;
    }

    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    hsp = Math.sqrt(
        0.299 * (r * r) +
        0.587 * (g * g) +
        0.114 * (b * b)
    );

    // Using the HSP value, determine whether the color is light or dark
    if (hsp > 127.5) {

        return 'light';
    } else {

        return 'dark';
    }
}

const toBase64 = file => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.readAsArrayBuffer(file);
	reader.onload = () => resolve(btoa(String.fromCharCode.apply(null, new Uint8Array(reader.result))));
	reader.onerror = error => reject(error);
});
